import { For, Show, createMemo, type Component, type JSX } from 'solid-js';
import { Text } from '~ui/text';
import { formatCurrency, formatPercent, perMonth, perYear, sumOrNull } from '~/lib/format';
import { LOAN_KIND_LABEL, loanAvailable, type Loan } from '~/data/portfolios';
import {
    accounts,
    allocatedTo,
    allocatedTotal,
    deductibleShare,
    loanDeductibleInterest,
    loanIncome,
    loanInterest,
    loanPositions,
    unallocated,
} from '~/stores/portfolio-store';
import { price } from '~data';

// Everything known about one loan, as a column of rows — the same shape the
// account and fund panels use.
//
// Two things here are typed and everything else is derived. Balance and rate are
// facts only you know; where the money went is recorded on the trades that spent
// it, so every figure below them is read off the blotter and none of it can
// disagree with the list of buys on the right.

const Fact: Component<{ label: string; children: JSX.Element; hint?: string }> = (props) => (
    <div class='flex h-row items-center justify-between gap-2 border-b px-2 whitespace-nowrap' title={props.hint}>
        <Text tone='muted'>{props.label}</Text>
        {props.children}
    </div>
);

const Section: Component<{ children: string }> = (props) => (
    <div class='flex h-row items-center border-b bg-muted px-2'>
        <Text weight='medium'>{props.children}</Text>
    </div>
);

const signed = (v: number | null): 'green' | 'red' | undefined => (v == null ? undefined : v >= 0 ? 'green' : 'red');

export const LoanFacts: Component<{ loan: Loan }> = (props) => {
    const positions = createMemo(() => loanPositions(props.loan));

    /** What the borrowed money bought is worth now, at market. */
    const value = createMemo(() =>
        sumOrNull(
            positions().map((p) => {
                const unit = price(p.symbol);
                return unit == null ? null : p.units * unit;
            }),
        ),
    );

    const cost = createMemo(() => allocatedTotal(props.loan));
    const gain = createMemo(() => {
        const now = value();
        return now == null ? null : now - cost();
    });

    const income = createMemo(() => loanIncome(props.loan));
    const rate = () => props.loan.rate;

    /** What the borrowed money yields against what it bought — not the book's
        yield. The whole point of recording which loan paid for a trade. */
    const borrowedYield = createMemo(() => {
        const paid = income();
        return paid == null || props.loan.balance <= 0 ? null : (paid / props.loan.balance) * 100;
    });
    const spread = createMemo(() => {
        const earned = borrowedYield();
        return earned == null ? null : earned - rate();
    });
    /** Dollars a month, which is the thing you can actually spend — a 1.7% gap
        says nothing about whether it is worth the risk until you know it is
        eleven dollars. */
    const profit = createMemo(() => {
        const paid = income();
        return paid == null ? null : (paid - loanInterest(props.loan)) / 12;
    });

    /** Which accounts this loan reached, and how much it put in each. Named
        rather than counted: whether the interest is deductible turns entirely on
        which side of that line the money landed. */
    const intoAccounts = createMemo(() =>
        accounts()
            .map((a) => ({ account: a, cost: allocatedTo(props.loan, a.id) }))
            .filter((row) => row.cost > 0)
            .sort((x, y) => y.cost - x.cost),
    );

    return (
        <>
            <Section>The facility</Section>
            {/* Read-only, every row of it. These are defined once, in Profile,
                because a facility is a fact about you rather than about the book
                on screen — and a rate you can type in two places is a rate that
                is current in one of them. */}
            <Show when={props.loan.issuer}>
                <Fact label='Issuer'>{props.loan.issuer}</Fact>
            </Show>
            <Fact label='Type'>{LOAN_KIND_LABEL[props.loan.kind]}</Fact>
            <Fact label='Drawn'>
                <Text weight='medium'>{formatCurrency(props.loan.balance)}</Text>
            </Fact>
            <Show when={props.loan.limit != null}>
                <Fact label='Limit'>{formatCurrency(props.loan.limit)}</Fact>
                <Fact label='Available' hint='What is left to draw on this facility'>
                    <Text tone='green'>{formatCurrency(loanAvailable(props.loan))}</Text>
                </Fact>
            </Show>
            <Fact label='Rate'>{formatPercent(props.loan.rate, 2)}</Fact>
            <Fact label='Interest / mo'>
                <Text tone='red'>{perMonth(loanInterest(props.loan))}</Text>
            </Fact>
            <Fact label='Interest / yr'>
                <Text tone='red'>{perYear(loanInterest(props.loan))}</Text>
            </Fact>

            <Section>Invested</Section>
            <Fact label='Put to work' hint='Cost of what this loan bought and still holds'>
                {formatCurrency(cost())}
            </Fact>
            {/* Negative means more has been bought on this loan than its balance
                covers. Shown rather than clamped: it is a data error worth
                seeing, and it is also what a stale balance box looks like. */}
            <Fact label='Uninvested' hint='Drawn but not yet spent — it earns nothing and its interest is not deductible'>
                <Text tone={unallocated(props.loan) < 0 ? 'red' : undefined}>{formatCurrency(unallocated(props.loan))}</Text>
            </Fact>
            <Fact label='Market value' hint='What the borrowed share of those positions is worth now'>
                <Text weight='medium'>{formatCurrency(value())}</Text>
            </Fact>
            <Fact label='Gain'>
                <Text tone={signed(gain())}>{formatCurrency(gain())}</Text>
            </Fact>
            <Fact label='Positions'>{positions().length}</Fact>

            <Section>Carry</Section>
            <Fact label='Dividends / mo' hint='What the positions this loan paid for pay, before its interest'>
                <Text tone='green'>{perMonth(income())}</Text>
            </Fact>
            <Fact label='Yield' hint='What the borrowed money earns, against the amount borrowed'>
                {formatPercent(borrowedYield(), 2)}
            </Fact>
            <Fact label='Spread' hint='That yield less the rate you pay — pre-tax'>
                <Text tone={signed(spread())}>{formatPercent(spread(), 2)}</Text>
            </Fact>
            {/* The verdict. Dollars rather than the rate gap, because the gap is
                not the thing you can spend. */}
            <Fact label='Profit / mo' hint='Dividends less interest, per month, before tax'>
                <Text tone={signed(profit())} weight='medium'>
                    {formatCurrency(profit())}
                </Text>
            </Fact>

            <Section>Deductibility</Section>
            {/* Not a choice. It falls out of the account the borrowed money was
                invested in: a registered account earns tax-exempt income, so
                sheltering the income sheltered the deduction with it. Money
                drawn and not spent qualifies for nothing either. */}
            <Fact label='Deductible share'>
                <Text tone={deductibleShare(props.loan) > 0 ? 'green' : 'muted'}>{formatPercent(deductibleShare(props.loan) * 100, 0)}</Text>
            </Fact>
            <Fact label='Deductible / yr'>
                <Text tone={deductibleShare(props.loan) > 0 ? 'green' : 'muted'}>{formatCurrency(loanDeductibleInterest(props.loan))}</Text>
            </Fact>

            <Show when={intoAccounts().length > 0}>
                <Section>Where it went</Section>
                <For each={intoAccounts()}>
                    {(row) => (
                        <Fact label={row.account.label} hint={row.account.sheltered ? 'Registered — this share buys no deduction' : 'Taxable — this share is what makes the interest deductible'}>
                            <Text tone={row.account.sheltered ? undefined : 'green'}>{formatCurrency(row.cost)}</Text>
                        </Fact>
                    )}
                </For>
            </Show>
        </>
    );
};

export default LoanFacts;
